import { useState } from 'react';
import {
  MapPinIcon,
  ComputerDesktopIcon,
  HomeIcon,
  EllipsisVerticalIcon,
  CalendarDaysIcon,
  ClockIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../../ui/Card';
import { Button } from '../../ui/Button';
import type { SavedMeeting } from '../../../types/meetings';

const DAY_LABELS: Record<string, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

function formatTime(time: string): string {
  const [hourStr, min] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

interface MeetingCardProps {
  meeting: SavedMeeting;
  onLogAttendance: (meeting: SavedMeeting) => void;
  onEdit: (meeting: SavedMeeting) => void;
  onDelete: (meeting: SavedMeeting) => void;
}

export function MeetingCard({ meeting, onLogAttendance, onEdit, onDelete }: MeetingCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const formatIcon =
    meeting.format === 'online' ? (
      <ComputerDesktopIcon className="w-4 h-4" />
    ) : meeting.format === 'hybrid' ? (
      <span className="flex items-center gap-0.5">
        <MapPinIcon className="w-4 h-4" />
        <ComputerDesktopIcon className="w-4 h-4" />
      </span>
    ) : (
      <MapPinIcon className="w-4 h-4" />
    );

  const locationLine =
    meeting.format === 'in-person' || meeting.format === 'hybrid'
      ? [meeting.address, meeting.city].filter(Boolean).join(', ')
      : meeting.zoomLink
        ? `Zoom${meeting.zoomMeetingId ? ` · ID: ${meeting.zoomMeetingId}` : ''}`
        : null;

  return (
    <Card variant="default" padding="sm" className="relative">
      <CardContent>
        <div className="flex items-start justify-between gap-3">
          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-surface-900 dark:text-surface-100 truncate">
                {meeting.name}
              </h3>
              {meeting.isHomeGroup && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gold-100 text-gold-800 dark:bg-gold-900/30 dark:text-gold-300">
                  <HomeIcon className="w-3 h-3" />
                  Home Group
                </span>
              )}
            </div>

            {/* Fellowship badge */}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                {meeting.fellowshipName}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-surface-500 dark:text-surface-400 capitalize">
                {formatIcon}
                {meeting.format}
              </span>
            </div>

            {/* Days + time */}
            <div className="flex items-center gap-3 mt-2 text-sm text-surface-600 dark:text-surface-400">
              <span className="flex items-center gap-1">
                <CalendarDaysIcon className="w-4 h-4 flex-shrink-0" />
                {meeting.dayOfWeek.map(d => DAY_LABELS[d] ?? d).join(', ')}
              </span>
              {meeting.time && (
                <span className="flex items-center gap-1">
                  <ClockIcon className="w-4 h-4 flex-shrink-0" />
                  {formatTime(meeting.time)}
                </span>
              )}
            </div>

            {/* Location or Zoom */}
            {locationLine && (
              <p className="mt-1 text-xs text-surface-500 dark:text-surface-400 truncate">
                {locationLine}
              </p>
            )}
          </div>

          {/* Action menu */}
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              className="p-1.5 rounded-lg text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
              aria-label="Meeting options"
            >
              <EllipsisVerticalIcon className="w-5 h-5" />
            </button>
            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-8 z-20 w-44 bg-white dark:bg-surface-800 rounded-xl shadow-lg border border-surface-200 dark:border-surface-700 py-1">
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); onEdit(meeting); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-surface-700 dark:text-surface-200 hover:bg-surface-50 dark:hover:bg-surface-700"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                    Edit meeting
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); onDelete(meeting); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete meeting
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Log attendance button */}
        <div className="mt-3 pt-3 border-t border-surface-100 dark:border-surface-700">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<CheckCircleIcon className="w-4 h-4" />}
            onClick={() => onLogAttendance(meeting)}
          >
            Log Attendance
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
